// src/services/scim-service.js
// Self-service SCIM2 calls against the logged-in user's own Asgardeo profile.
//
// These hit the /scim2/Me endpoint using the user's own access token, so no
// M2M application or backend proxy is needed. /Me only ever exposes the
// caller's own profile, so a user can never read or modify anyone else's.

const SCIM_ME_URL = `${import.meta.env.VITE_ASGARDEO_BASE_URL}/scim2/Me`;
const SCIM_PATCH_OP_SCHEMA = 'urn:ietf:params:scim:api:messages:2.0:PatchOp';

/**
 * Reads the current user's SCIM profile.
 * @param {string} accessToken - Asgardeo access token for the logged-in user.
 * @returns {Promise<object>} The SCIM user resource.
 */
export const getMyProfile = async (accessToken) => {
  const response = await fetch(SCIM_ME_URL, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/scim+json',
    },
  });

  if (!response.ok) {
    throw new Error(`SCIM profile fetch failed: HTTP ${response.status}`);
  }

  return response.json();
};

/**
 * Extracts the mobile phone number from a SCIM user resource.
 * @param {object} profile - SCIM user resource from getMyProfile().
 * @returns {string} The mobile number, or '' if none is set.
 */
export const getMobileFromProfile = (profile) => {
  const phoneNumbers = profile?.phoneNumbers || [];
  const mobile = phoneNumbers.find((entry) => entry.type === 'mobile');
  return mobile?.value || '';
};

/**
 * Updates the current user's mobile number via a SCIM PATCH on /scim2/Me.
 * @param {string} accessToken - Asgardeo access token for the logged-in user.
 * @param {string} mobileNumber - Number in E.164 format, e.g. +94771234567.
 * @returns {Promise<object>} The updated SCIM user resource.
 */
export const updateMyMobileNumber = async (accessToken, mobileNumber) => {
  const body = {
    schemas: [SCIM_PATCH_OP_SCHEMA],
    Operations: [
      {
        op: 'replace',
        value: {
          phoneNumbers: [{ type: 'mobile', value: mobileNumber }],
        },
      },
    ],
  };

  const response = await fetch(SCIM_ME_URL, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/scim+json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    throw new Error(`SCIM mobile update failed: HTTP ${response.status} ${detail}`);
  }

  return response.json();
};
